import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, leagueAPI, levelsAPI, messageAPI, clubAPI, interestedUsersAPI } from '../services/api';
import ChatBox from '../components/ChatBox';
import './PlayerPage.css';

const PlayerPage = () => {
  const { userId } = useParams();
  const { user: loggedInUser, isAdmin } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [joinedLeagues, setJoinedLeagues] = useState([]);
  const [createdLeagues, setCreatedLeagues] = useState([]);
  const [interestedLeagues, setInterestedLeagues] = useState([]);
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [levelEvents, setLevelEvents] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(null); // Current snapshot level for selected sport
  const [showAllLeagues, setShowAllLeagues] = useState(false);
  const [selectedSport, setSelectedSport] = useState(loggedInUser?.defaultSport || 'Tennis (singles)');
  const [sportIcon, setSportIcon] = useState('');
  const [chatBoxOpen, setChatBoxOpen] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [myClubs, setMyClubs] = useState([]);
  
  // Check if user is viewing their own profile
  const isOwnProfile = loggedInUser && loggedInUser.userId === userId;
  // Check if user has permission to view other profiles (admins always have permission)
  const canViewProfile = isOwnProfile || isAdmin || (loggedInUser?.canViewProfiles ?? true);

  useEffect(() => {
    // Don't load data if user doesn't have permission (unless it's their own profile)
    if (!canViewProfile && !isOwnProfile) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [userRes, leaguesRes, createdLeaguesRes, statsRes, matchesRes, clubsRes] = await Promise.all([
          userAPI.getUser(userId),
          userAPI.getUserLeagues(userId),
          userAPI.getCreatedLeagues(userId),
          userAPI.getUserStats(userId),
          userAPI.getUserMatches(userId),
          isOwnProfile ? clubAPI.getMyClubs().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        // NEW: Use interestedUsersAPI for better scalability
        const interestedRes = await interestedUsersAPI.getUserInterestedLeagues(userId, 50)
          .catch((error) => {
            console.error('Failed to load interested leagues:', error);
            return { data: { leagues: [] } };
          });

        if (!isMounted) return;
        const userData = userRes.data;
        setProfileUser(userData);
        // Ensure sport filter is set to logged-in user's default sport
        if (loggedInUser?.defaultSport) {
          setSelectedSport(loggedInUser.defaultSport);
        } else if (!selectedSport) {
          setSelectedSport('Tennis (singles)');
        }
        const leagues = (leaguesRes.data || []).filter(l => !l?.superLeagueId); // hide subleagues; show only regular + super leagues
        setJoinedLeagues(leagues);
        const created = (createdLeaguesRes.data || []).filter(l => !l?.superLeagueId); // hide subleagues; show only regular + super leagues
        setCreatedLeagues(created);
        const interested = interestedRes.data.leagues || interestedRes.data || [];
        setInterestedLeagues(interested);
        setStats(statsRes.data || null);
        setMatches(matchesRes.data || []);
        if (isOwnProfile) {
          setMyClubs(clubsRes.data || []);
        }
        // Level events will be loaded when selectedSport is set
      } catch (e) {
        if (!isMounted) return;
        console.error('Failed to load player page:', e);
        setError('Failed to load player profile');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [userId, canViewProfile, isOwnProfile]);

  useEffect(() => {
    const sportIcons = {
      'Tennis (singles)': '🎾',
      'Tennis (doubles)': '🎾',
      Padel: '🏓',
      Squash: '🏸',
      Football: '⚽',
      Basketball: '🏀',
    };

    if (selectedSport && sportIcons[selectedSport]) {
      setSportIcon(sportIcons[selectedSport]);
    } else {
      setSportIcon('');
    }
  }, [selectedSport]);

  // Load level events and current snapshot when sport changes
  useEffect(() => {
    if (!selectedSport || !userId || !canViewProfile && !isOwnProfile) return;
    
    let isMounted = true;
    const loadLevelData = async () => {
      try {
        const [levelsRes, currentRes] = await Promise.all([
          levelsAPI.getUserLevels(userId, selectedSport),
          levelsAPI.checkUserLevel(userId, selectedSport).catch(() => ({ data: { hasLevel: false, currentLevel: null } }))
        ]);
        if (isMounted) {
          setLevelEvents(levelsRes.data.events || []);
          // If there are events, use the latest postLevel; otherwise use current snapshot if available
          const events = levelsRes.data.events || [];
          if (events.length > 0) {
            setCurrentLevel(events[events.length - 1].postLevel);
          } else if (currentRes.data.hasLevel && currentRes.data.currentLevel !== undefined) {
            setCurrentLevel(currentRes.data.currentLevel);
          } else {
            setCurrentLevel(null);
          }
        }
      } catch (err) {
        console.error('Failed to load level data:', err);
        if (isMounted) {
          setLevelEvents([]);
          setCurrentLevel(null);
        }
      }
    };
    
    loadLevelData();
    return () => { isMounted = false; };
  }, [selectedSport, userId, canViewProfile, isOwnProfile]);

  const LevelChart = ({ events }) => {
    if (!events || events.length === 0) return <div style={{ color: '#777' }}>No level history yet.</div>;
    // Expanded dimensions to fill the card
    const width = 1200;
    const height = 400;
    const padding = 50;
    // Limit to at most 10 points: always include first (starting level) and last (current level), pick 8 evenly spaced in between
    let selected = events;
    if (events.length > 10) {
      const lastIdx = events.length - 1;
      const picks = [0]; // Always include first (starting level)
      const innerNeeded = 8; // 8 points between first and last
      // Calculate evenly spaced indices between first and last
      for (let k = 1; k <= innerNeeded; k++) {
        const idx = Math.round((k * lastIdx) / (innerNeeded + 1));
        if (!picks.includes(idx)) picks.push(idx);
      }
      picks.push(lastIdx); // Always include last (current level)
      picks.sort((a, b) => a - b);
      // Remove duplicates and ensure we have at most 10 points
      const uniquePicks = [...new Set(picks)];
      selected = uniquePicks.slice(0, 10).map(i => events[i]);
    }
    const xs = selected.map((_, i) => i);
    const ys = selected.map(e => e.postLevel ?? 0);
    const dates = selected.map(e => (e.timestamp ? (e.timestamp.split('#')[0] || e.timestamp) : ''));
    const minY = Math.min(0, ...ys);
    const maxY = Math.max(100, ...ys);
    // Use selected.length for scaling, not events.length, to properly space the 10 points
    const xScale = (i) => padding + (i * (width - 2 * padding)) / Math.max(1, selected.length - 1);
    const yScale = (v) => height - padding - ((v - minY) * (height - 2 * padding)) / (maxY - minY || 1);
    const pathD = xs.map((i, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(ys[idx])}`).join(' ');
    return (
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: '100%', height: 'auto', minHeight: '400px' }}>
        {/* X-axis */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#e5e7eb" strokeWidth="2" />
        {/* Line path */}
        <path d={pathD} fill="none" stroke="#2563eb" strokeWidth="3" />
        {/* Points and level labels */}
        {xs.map((i, idx) => (
          <g key={`pt-${i}`}>
            <circle cx={xScale(i)} cy={yScale(ys[idx])} r="7" fill="#2563eb" stroke="#1e40af" strokeWidth="2" />
            <text
              x={xScale(i)}
              y={yScale(ys[idx]) - 18}
              textAnchor="middle"
              fontSize="16"
              fontWeight="600"
              fill="#111827"
            >
              {Math.round(ys[idx])}
            </text>
          </g>
        ))}
        {/* Date ticks */}
        {xs.map((i) => (
          <g key={`tick-${i}`}>
            <line x1={xScale(i)} y1={height - padding} x2={xScale(i)} y2={height - padding + 6} stroke="#9ca3af" strokeWidth="2" />
            <text
              x={xScale(i)}
              y={height - padding + 28}
              textAnchor="middle"
              fontSize="13"
              fill="#374151"
              fontWeight="500"
            >
              {dates[i] ? new Date(dates[i]).toLocaleDateString() : ''}
            </text>
          </g>
        ))}
      </svg>
      </div>
    );
  };

  // Show warning page if user doesn't have permission to view other profiles
  if (!canViewProfile && !isOwnProfile && !loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="card" style={{ maxWidth: '600px', margin: '40px auto', textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
            <h1 style={{ marginBottom: '16px', color: '#111827' }}>Profile Access Restricted</h1>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
              You don't have permission to view other user profiles. To access this feature, please contact support to upgrade your account.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/support" className="btn btn-primary">
                Contact Support
              </Link>
              <Link to="/leagues" className="btn btn-secondary">
                Back to Leagues
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading player...</div>
      </div>
    );
  }

  if (error || !profileUser) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">{error || 'Player not found'}</div>
          <Link to="/leagues" className="btn btn-primary">Back to Leagues</Link>
        </div>
      </div>
    );
  }

  // Filter data based on selected sport
  const filteredJoinedLeagues = joinedLeagues.filter(league => league.sport === selectedSport);
  const filteredCreatedLeagues = createdLeagues.filter(league => league.sport === selectedSport);
  const filteredInterestedLeagues = interestedLeagues.filter(league => league.sport === selectedSport);
  const filteredMatches = matches.filter(match => match.sport === selectedSport || match.leagueSport === selectedSport);
  const filteredLevelEvents = levelEvents.filter(event => event.sport === selectedSport || event.leagueSport === selectedSport);
  
  // Calculate filtered stats
  const filteredStats = stats ? {
    ...stats,
    totalMatches: filteredMatches.length,
    wins: filteredMatches.filter(m => m.winnerId === userId).length,
    losses: filteredMatches.filter(m => m.winnerId !== userId && (m.player1Id === userId || m.player2Id === userId)).length,
    winRate: filteredMatches.length > 0 
      ? filteredMatches.filter(m => m.winnerId === userId).length / filteredMatches.length 
      : 0,
    leaguesParticipated: new Set(filteredJoinedLeagues.map(l => l.leagueId)).size,
    leaguesWon: 0, // Would need to calculate from filtered leagues
  } : stats;

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header player-page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{profileUser.name}</h1>
            {!isOwnProfile && loggedInUser && (
              <button
                className="message-link-button"
                onClick={() => {
                  setSelectedChatUser({
                    userId: profileUser.userId,
                    name: profileUser.name,
                  });
                  setChatBoxOpen(true);
                }}
                title="Send message"
                aria-label={`Send message to ${profileUser.name}`}
              >
                💬+
              </button>
            )}
          </div>
          <div className="player-sport-filter">
            <div className="filter-group">
              <label className="form-label">Sport</label>
              <div className="sport-select-wrapper">
                <select
                  className="form-input"
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                >
                  <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                  <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                  <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                  <option value="Squash">🏸 Squash</option>
                  <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                  <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics - Full Width */}
        {filteredStats && (
          <div className="card stats-card">
            <h2>Statistics</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{filteredStats.totalMatches}</div>
                <div className="stat-label">Matches Played</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{Math.round((filteredStats.winRate || 0) * 100)}%</div>
                <div className="stat-label">Win Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{filteredStats.leaguesParticipated}</div>
                <div className="stat-label">Leagues Participated</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{filteredStats.leaguesWon}</div>
                <div className="stat-label">Titles Won</div>
              </div>
            </div>
          </div>
        )}

        {/* Level - Full Width */}
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              Level
              {(() => {
                // Only show level if user has one (from events or questionnaire)
                const hasLevel = filteredLevelEvents.length > 0 || currentLevel !== null;
                let displayLevel = null;
                
                if (filteredLevelEvents.length > 0) {
                  displayLevel = filteredLevelEvents[filteredLevelEvents.length - 1].postLevel;
                } else if (currentLevel !== null) {
                  displayLevel = currentLevel;
                }
                
                // Only show level badge if user actually has a level
                if (hasLevel && displayLevel !== null) {
                  return (
                <span style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: '#2563eb',
                  background: '#dbeafe',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                      {Math.round(displayLevel)}
                </span>
                  );
                }
                return null;
              })()}
               {loggedInUser && loggedInUser.userId === userId && filteredLevelEvents.length === 0 && currentLevel === null && (
                <Link 
                   to={`/questionnaire?sport=${encodeURIComponent(selectedSport)}`}
                  className="btn btn-primary"
                  style={{ 
                    fontSize: '14px',
                    padding: '8px 16px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  Fill Questionnaire
                </Link>
              )}
            </h2>
          </div>
          <div style={{ padding: '16px 0' }}>
            <LevelChart events={filteredLevelEvents} />
          </div>
        </div>

        {/* Middle Row: Leagues and Expressed Interest */}
        <div className="profile-grid" style={{ marginTop: '24px' }}>
          <div className="card">
            <div className="card-header">
              <h2>Leagues</h2>
            </div>
            {(() => {
              // Combine joined and created leagues, removing duplicates
              const leagueMap = new Map();
              filteredJoinedLeagues.forEach(league => {
                leagueMap.set(league.leagueId, league);
              });
              filteredCreatedLeagues.forEach(league => {
                if (!leagueMap.has(league.leagueId)) {
                  leagueMap.set(league.leagueId, league);
                }
              });
              const allLeagues = Array.from(leagueMap.values());
              
              if (allLeagues.length === 0) {
                return (
                  <div className="empty-state">
                    <p>This player hasn't joined or created any leagues yet.</p>
                  </div>
                );
              }
              
              return (
                <>
                  <div className="leagues-list">
                    {(showAllLeagues ? allLeagues : allLeagues.slice(-3)).map((league) => {
                      const isPlayer = Array.isArray(league.players) && league.players.some(p => {
                        if (typeof p === 'string') return p === userId;
                        if (typeof p === 'object') return p.userId === userId;
                        return false;
                      });
                      const isAdmin = league.createdBy === userId;
                      const roles = [];
                      if (isPlayer) roles.push('Player');
                      if (isAdmin) roles.push('Admin');
                      
                      return (
                        <Link
                          key={league.leagueId}
                          to={league.isSuperLeague ? `/super-leagues/${league.leagueId}` : `/leagues/${league.leagueId}`}
                          className="league-item"
                        >
                          <div className="league-info">
                            <h3>{league.name}</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                              <span className="badge badge-info">{league.sport}</span>
                              {roles.length > 0 && (
                                <span className="badge badge-success" style={{ fontSize: '11px' }}>
                                  {roles.join(' & ')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="league-arrow">→</span>
                        </Link>
                      );
                    })}
                  </div>
                  {allLeagues.length > 3 && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowAllLeagues(!showAllLeagues)}
                      style={{ marginTop: '16px', width: '100%' }}
                    >
                      {showAllLeagues ? 'Show Less' : `Show More (${allLeagues.length - 3} more)`}
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Expressed Interest</h2>
            </div>
            {filteredInterestedLeagues.length === 0 ? (
              <div className="empty-state">
                <p>No pending interests.</p>
              </div>
            ) : (
              <div className="leagues-list">
                {filteredInterestedLeagues.map((league) => (
                  <Link
                    key={league.leagueId}
                    to={league.isSuperLeague ? `/super-leagues/${league.leagueId}` : `/leagues/${league.leagueId}`}
                    className="league-item"
                  >
                    <div className="league-info">
                      <h3>{league.name}</h3>
                      <span className="badge badge-info">{league.sport}</span>
                      <span className="badge badge-warning" style={{ marginLeft: '8px' }}>Pending</span>
                    </div>
                    <span className="league-arrow">→</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Second Row: Clubs and Recent Matches */}
        <div className="profile-grid" style={{ marginTop: '24px' }}>
          {/* Clubs Card - Only show for own profile */}
          {isOwnProfile && (
            <div className="card">
              <div className="card-header">
                <h2>Clubs</h2>
              </div>
              {myClubs.length === 0 ? (
                <div className="empty-state">
                  <p>No clubs yet.</p>
                </div>
              ) : (
                <div className="clubs-list">
                  {myClubs.map((club) => (
                    <Link
                      key={club.clubId}
                      to={`/clubs/${club.clubId}`}
                      className="club-item"
                    >
                      <div className="club-item-info">
                        <h3>{club.name}</h3>
                        <p className="club-item-meta">
                          {club.members?.length || 0} member{club.members?.length !== 1 ? 's' : ''}
                          {club.isAdmin && (
                            <span className="admin-badge" style={{ marginLeft: '8px' }}>Admin</span>
                          )}
                        </p>
                      </div>
                      <span className="club-item-arrow">→</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Recent Matches Card - Always show */}
          <div className="card">
            <div className="card-header">
              <h2>Recent Matches</h2>
            </div>
            {filteredMatches.length === 0 ? (
              <div className="empty-state">
                <p>No matches yet.</p>
              </div>
            ) : (
              <div className="matches-list-container">
                {(showAllMatches ? filteredMatches : filteredMatches.slice(0, 3)).map((match) => {
                  const isPlayer1 = match.player1Id === userId;
                  const opponent = isPlayer1 ? match.player2Name : match.player1Name;
                  const isWinner = match.winnerId === userId;
                  return (
                    <div key={match.matchId} className="match-history-item">
                      <div className="match-history-info">
                        <div className="match-history-header">
                          <span className={`match-result ${isWinner ? 'win' : 'loss'}`}>
                            {isWinner ? 'W' : 'L'}
                          </span>
                          <span className="match-opponent">vs {opponent}</span>
                          <span className="badge badge-info">{match.leagueName}</span>
                        </div>
                        {match.matchDate && (
                          <div className="match-history-date">
                            {new Date(match.matchDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredMatches.length > 3 && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowAllMatches(!showAllMatches)}
                    style={{ marginTop: '16px', width: '100%' }}
                  >
                    {showAllMatches ? 'Show Less' : `Show All (${filteredMatches.length})`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {chatBoxOpen && selectedChatUser && (
        <ChatBox
          otherUserId={selectedChatUser.userId}
          otherUserName={selectedChatUser.name}
          onClose={() => {
            setChatBoxOpen(false);
            setSelectedChatUser(null);
          }}
        />
      )}
    </div>
  );
};

export default PlayerPage;


